import { Box, CircularProgress, keyframes, Stack, SxProps, Theme } from '@mui/material';
import { stableStringify } from '@provision/formatting-utils';
import { useRef, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { FormLabel } from 'src/components/Forms/FormLabel/form-label';
import { PillRow } from 'src/components/Pill';
import { SIDE_PANEL_ROW_CONTENT_HEIGHT, SidePanelRow } from 'src/components/SidePanelRow';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import type {
  CustomAttributeValue,
  NormalizedCustomAttributeDefinition,
} from 'src/graphql/entities/custom-attributes/custom-attributes.types';
import { getCustomAttributeConfig } from 'src/graphql/entities/custom-attributes/field-view-registry';
import { CustomAttributeRowData } from 'src/graphql/entities/custom-attributes/field-view-registry/types';
import { useSelectedPartner } from 'src/graphql/entities/partner/partner';
import { useDelayedFlag } from 'src/utils/hooks/use-delayed-flag';
import { useDebouncedAttributeSave } from './use-debounced-attribute-save';

interface CustomAttributeRowProps {
  row: CustomAttributeRowData;
  onSave: (
    definition: NormalizedCustomAttributeDefinition,
    value: CustomAttributeValue,
  ) => void;
  disabled: boolean;
}

const valueFadeIn = keyframes`
  from { opacity: 0.35; }
  to { opacity: 1; }
`;

const SPINNER_DELAY_MS = 250;

// The control fills the row's content height, so a custom attribute keeps the same
// pitch, hover surface and text size as a built-in property row.
const ROW_CONTROL_MIN_HEIGHT = SIDE_PANEL_ROW_CONTENT_HEIGHT;

// Matches the horizontal padding the inline SelectMenu trigger applies to itself, so a row
// rendering plain text starts at the same x as one rendering a trigger. It sits inside the
// row's hover surface, which stays the full width of the value column either way.
const VALUE_INSET_X = 1;

const ROW_STYLES: SxProps<Theme> = (theme) => ({
  minHeight: ROW_CONTROL_MIN_HEIGHT,
  '& .MuiOutlinedInput-root': {
    fontSize: '0.8rem',
  },
  '& .MuiOutlinedInput-input:not(.MuiInputBase-inputMultiline)': {
    paddingTop: '0.25rem',
    paddingBottom: '0.25rem',
  },
  // mobile Safari zooms when focusing inputs with computed font-size below ~16px;
  // force the editor font-size up at the `sm` breakpoint to prevent the auto-zoom.
  [theme.breakpoints.down('sm')]: {
    '& .MuiInputBase-input': {
      fontSize: theme.utils?.important?.('1rem') ?? '1rem !important',
    },
  },
});

const VALUE_SIDE_STYLES = {
  borderRadius: 1,
  transition: 'background-color 150ms ease',
  '&:hover': {
    backgroundColor: 'custom.OV.sys.surface',
  },
};

export const CustomAttributeRow = ({ row, onSave, disabled }: CustomAttributeRowProps) => {
  const { definition } = row;
  const { selectedPartner } = useSelectedPartner();
  const field = getCustomAttributeConfig(definition.type);
  const canEdit = !disabled && field.editable;
  const isDirectEdit = canEdit && field.editable && field.directEdit === true;
  const [isEditing, setIsEditing] = useState(false);
  const isInteractive = canEdit && !isEditing && !isDirectEdit;
  const [draft, setDraft] = useState(row.parsedValue);
  const triggerRef = useRef<HTMLDivElement>(null);
  const showSpinner = useDelayedFlag(row.isSaving, SPINNER_DELAY_MS);

  const { displayedValue, submit: setPendingValue } = useDebouncedAttributeSave({
    definition,
    serverValue: row.parsedValue,
    isSaving: row.isSaving,
    onSave,
  });

  const startEdit = () => {
    setDraft(displayedValue);
    setIsEditing(true);
  };

  const closeEdit = () => {
    setIsEditing(false);
    queueMicrotask(() => triggerRef.current?.focus());
  };

  const submit = (value: CustomAttributeValue) => {
    if (!isDirectEdit) {
      closeEdit();
    }
    setPendingValue(value);
  };

  const { View } = field;
  const timezone = selectedPartner?.timeZone || undefined;
  const Editor = (isEditing || isDirectEdit) && field.editable ? field.Editor : null;
  const layout = field.layout ?? 'columns';
  const editorValue = isDirectEdit ? displayedValue : draft;

  // While a save is in flight, swallow pointer events on the editor surface so
  // rapid clicks don't queue multiple saves — without any greyed-out styling.
  const busyOverlaySx = row.isSaving ? { pointerEvents: 'none' as const } : null;

  const insetValue = layout === 'columns' && !(Editor && field.insetByControl);

  const valueBox = (
    <Box
      ref={triggerRef}
      sx={{
        flexGrow: 1,
        minWidth: 0,
        px: insetValue ? VALUE_INSET_X : 0,
        cursor: isInteractive ? 'pointer' : 'default',
        ...busyOverlaySx,
      }}
      onClick={isInteractive ? startEdit : undefined}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : -1}
      aria-label={isInteractive ? `Edit ${definition.label}` : undefined}
      onKeyDown={
        isInteractive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                startEdit();
              }
            }
          : undefined
      }
    >
      {Editor ? (
        <Editor
          value={editorValue}
          definition={row.definition}
          timezone={timezone}
          onChange={setDraft}
          onSave={(next) => submit(next ?? editorValue)}
          onCancel={closeEdit}
          disabled={disabled}
          saving={showSpinner}
        />
      ) : (
        <Box
          key={stableStringify(displayedValue)}
          sx={{ animation: `${valueFadeIn} 150ms ease` }}
        >
          <View value={displayedValue} definition={row.definition} timezone={timezone} />
        </Box>
      )}
    </Box>
  );

  const sideAdornments = (
    <>
      {showSpinner && !isDirectEdit ? <CircularProgress size="0.875rem" /> : null}
      {row.error ? (
        <Tooltip title={row.error}>
          <Box display="inline-flex">
            <DynamicIcon name="error" sx={{ color: 'error.main', fontSize: '1rem' }} />
          </Box>
        </Tooltip>
      ) : null}
    </>
  );

  if (layout === 'flow') {
    // The editor's own pills and "+ Add" become flex siblings of the label, so
    // nothing may wrap them; a read-only user still gets the editor, which then
    // renders its pills without actions.
    return (
      <PillRow label={definition.label}>
        {field.editable ? (
          <field.Editor
            value={displayedValue}
            definition={row.definition}
            timezone={timezone}
            onChange={setDraft}
            onSave={(next) => setPendingValue(next ?? displayedValue)}
            onCancel={closeEdit}
            disabled={disabled}
            saving={showSpinner}
          />
        ) : (
          <View value={displayedValue} definition={row.definition} timezone={timezone} />
        )}
        {sideAdornments}
      </PillRow>
    );
  }

  if (layout === 'stacked') {
    return (
      <SidePanelRow direction="column" alignItems="stretch" gap={0.75} sx={ROW_STYLES}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <FormLabel label={definition.label} />
          <Box sx={{ marginLeft: 'auto' }} />
          {sideAdornments}
        </Stack>
        <Box sx={VALUE_SIDE_STYLES}>{valueBox}</Box>
      </SidePanelRow>
    );
  }

  return (
    <SidePanelRow alignItems="flex-start" sx={ROW_STYLES}>
      <FormLabel label={definition.label} />
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        sx={{
          width: '100%',
          minWidth: 0,
          minHeight: ROW_CONTROL_MIN_HEIGHT,
          ...VALUE_SIDE_STYLES,
        }}
      >
        {valueBox}
        {sideAdornments}
      </Stack>
    </SidePanelRow>
  );
};
